import { useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from "react";
import Highcharts from "highcharts";

const BG = "#0B1018";
const BID_GREEN = "#22C55E";
const BID_FILL = "rgba(34, 197, 94, 0.28)";
const ASK_RED = "#EF4444";
const ASK_FILL = "rgba(239, 68, 68, 0.28)";

function normalizeLevels(levels) {
  return (levels || [])
    .map((l) => ({ price: Number(l.price), qty: Number(l.qty) || 0 }))
    .filter((l) => l.price > 0 && l.qty > 0);
}

function bidSeriesData(bids) {
  const rows = normalizeLevels(bids).sort((a, b) => b.price - a.price);
  if (!rows.length) return [];
  let cum = 0;
  const depth = rows.map((r) => {
    cum += r.qty;
    return { price: r.price, cum };
  });
  return depth.sort((a, b) => a.price - b.price).map((d) => [d.price, d.cum]);
}

function askSeriesData(asks) {
  const rows = normalizeLevels(asks).sort((a, b) => a.price - b.price);
  if (!rows.length) return [];
  let cum = 0;
  return rows.map((r) => {
    cum += r.qty;
    return [r.price, cum];
  });
}

function fmtAxisPrice(v, quote) {
  if (!Number.isFinite(v)) return "";
  if (quote === "INR") return v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  if (v >= 1000) return v.toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (v >= 1) return v.toFixed(2);
  return v.toFixed(4);
}

function buildChartOptions(bidData, askData, mid, quote) {
  return {
    chart: {
      type: "area",
      backgroundColor: BG,
      animation: false,
      spacing: [12, 12, 8, 12],
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      type: "linear",
      minPadding: 0,
      maxPadding: 0,
      lineColor: "#1E293B",
      tickColor: "#1E293B",
      gridLineColor: "#172033",
      gridLineDashStyle: "Dash",
      labels: {
        style: { color: "#94A3B8", fontSize: "10px", fontFamily: "monospace" },
        formatter() {
          return fmtAxisPrice(this.value, quote);
        },
      },
      plotLines: mid
        ? [{
            color: "#475569",
            value: mid,
            width: 1,
            dashStyle: "Dash",
            zIndex: 3,
          }]
        : [],
    },
    yAxis: [
      {
        lineWidth: 1,
        lineColor: "#1E293B",
        gridLineColor: "#1E293B",
        gridLineWidth: 1,
        title: { text: undefined },
        tickWidth: 1,
        tickLength: 4,
        tickPosition: "inside",
        labels: {
          align: "left",
          x: 6,
          style: { color: BID_GREEN, fontSize: "10px", fontFamily: "monospace" },
        },
      },
      {
        opposite: true,
        linkedTo: 0,
        lineWidth: 1,
        lineColor: "#1E293B",
        gridLineWidth: 0,
        title: { text: undefined },
        tickWidth: 1,
        tickLength: 4,
        tickPosition: "inside",
        labels: {
          align: "right",
          x: -6,
          style: { color: ASK_RED, fontSize: "10px", fontFamily: "monospace" },
        },
      },
    ],
    plotOptions: {
      area: {
        fillOpacity: 0.28,
        lineWidth: 1.5,
        step: "center",
        marker: { enabled: false },
        animation: false,
        states: { hover: { lineWidth: 2 } },
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: "rgba(15,21,29,0.95)",
      borderColor: "#334155",
      style: { color: "#94A3B8", fontSize: "11px" },
      headerFormat: '<span style="font-size:10px">Price: {point.key}</span><br/>',
      pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>',
      valueDecimals: 4,
    },
    series: [
      {
        name: "Bids",
        data: bidData,
        color: BID_GREEN,
        fillColor: BID_FILL,
        yAxis: 0,
      },
      {
        name: "Asks",
        data: askData,
        color: ASK_RED,
        fillColor: ASK_FILL,
        yAxis: 1,
      },
    ],
  };
}

function destroyChart(chartRef) {
  try {
    chartRef.current?.destroy();
  } catch {
    /* ignore */
  }
  chartRef.current = null;
}

export default forwardRef(function DepthChart({
  bids = [],
  asks = [],
  quote = "USDT",
  pairKey = "",
}, ref) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const { bidData, askData, mid, hasData } = useMemo(() => {
    const bidData = bidSeriesData(bids);
    const askData = askSeriesData(asks);
    const bestBid = bidData.length ? bidData[bidData.length - 1][0] : null;
    const bestAsk = askData.length ? askData[0][0] : null;
    const mid = bestBid && bestAsk ? (bestBid + bestAsk) / 2 : bestBid ?? bestAsk;
    return {
      bidData,
      askData,
      mid,
      hasData: bidData.length > 0 || askData.length > 0,
    };
  }, [bids, asks]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !hasData) {
      if (!hasData) destroyChart(chartRef);
      return undefined;
    }

    const opts = buildChartOptions(bidData, askData, mid, quote);

    if (!chartRef.current) {
      chartRef.current = Highcharts.chart(el, opts);
    } else {
      chartRef.current.series[0]?.setData(bidData, false);
      chartRef.current.series[1]?.setData(askData, false);
      chartRef.current.xAxis[0]?.update({
        plotLines: mid
          ? [{ color: "#475569", value: mid, width: 1, dashStyle: "Dash", zIndex: 3 }]
          : [],
      }, false);
      chartRef.current.redraw(false);
    }

    const reflow = () => chartRef.current?.reflow();
    requestAnimationFrame(reflow);
    const ro = new ResizeObserver(reflow);
    ro.observe(el);

    return () => ro.disconnect();
  }, [bidData, askData, mid, quote, hasData, pairKey]);

  useEffect(() => () => destroyChart(chartRef), []);

  useImperativeHandle(ref, () => ({
    exportSvg() {
      if (!chartRef.current) return null;
      try {
        return chartRef.current.getSVG();
      } catch {
        return null;
      }
    },
  }), []);

  return (
    <div
      className="relative h-full w-full min-h-[280px]"
      style={{ background: BG }}
      data-testid="depth-chart"
    >
      {!hasData ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center text-[13px] text-slate-400"
          data-testid="depth-chart-empty"
        >
          Loading depth…
        </div>
      ) : null}
      <div ref={containerRef} className="h-full w-full" />
    </div>
  );
});
